import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollY } = useScroll();

  // Background fades in from transparent to deep green as user scrolls
  const backgroundColor = useTransform(
    scrollY,
    [0, 80],
    ["rgba(11, 26, 20, 0)", "rgba(11, 26, 20, 0.97)"]
  );

  const borderOpacity = useTransform(
    scrollY,
    [0, 80],
    ["rgba(196, 167, 125, 0)", "rgba(196, 167, 125, 0.15)"]
  );

  const navItems = [
    { name: "Services", href: "/#services" },
    { name: "Locations", href: "/#locations" },
    { name: "Events", href: "/events" },
    { name: "Contact", href: "/#contact" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.includes("#")) {
      const hash = href.split("#")[1]; // Get the part after #
      if (location.pathname === "/") {
        // Already on homepage — smooth scroll
        e.preventDefault();
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: "smooth" });
      } else {
        // Navigate to homepage with hash, then scroll
        e.preventDefault();
        navigate(`/#${hash}`);
        // Small delay to ensure navigation completes before scrolling
        setTimeout(() => {
          const el = document.getElementById(hash);
          el?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
      setIsOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <motion.nav
      style={ { backgroundColor, borderBottomColor: borderOpacity }}
      className="fixed w-full z-50 border-b transition-colors duration-300"
      id="navbar"
    >
      <div
        className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center"
      >
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 border border-[#C4A77D] flex items-center justify-center">
            <span className="text-[#C4A77D] font-serif text-xl">ر</span>
          </div>
          <div className="hidden sm:block">
            <h1 className="text-sm font-light tracking-[0.2em] text-[#C4A77D] uppercase">Raha-ídúù</h1>
            <p className="text-[8px] tracking-widest text-gray-500 uppercase">Experience</p>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-8 text-xs tracking-widest uppercase font-light">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-gray-400 hover:text-[#C4A77D] transition-colors duration-200"
            >
              {item.name}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center">
          <Link
            to="/curate-experience"
            className="px-8 py-3 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all duration-300 hover:bg-white hover:border-white"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden text-[#C4A77D] p-1"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="lg:hidden bg-[#0b1a14]/98 border-b border-[#C4A77D]/10 px-6 pb-6 pt-4"
        >
          <div className="flex flex-col gap-5">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-gray-300 hover:text-[#C4A77D] transition-colors text-sm uppercase tracking-wider"
              >
                {item.name}
              </a>
            ))}
            <Link
              to="/curate-experience"
              className="inline-block px-6 py-3 border border-[#C4A77D] text-[#C4A77D] text-sm uppercase tracking-wider hover:bg-[#C4A77D] hover:text-black transition-all duration-300 text-center mt-2"
              onClick={() => setIsOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
